import type {
  ArenaGameplayDefinition,
  CardDefinition,
  ContentSnapshot,
  DeploymentZoneDefinition,
  EntityState,
  GroundRouteState,
  Movement,
  PlayerId,
  Rect,
  SpawnDefinition,
  TowerSocketId,
  UnitDefinition,
  WorldPoint,
} from './types';
import { ARENA_EDGE_INSET, clampToBounds, isPointInsideBounds } from './arena-runtime';
import { bridgeCorridor, footprintIntersectsRiver } from './navigation/geometry';

export interface PlannedSpawnGroup {
  definition: UnitDefinition;
  requestedPositions: readonly WorldPoint[];
  positions: readonly WorldPoint[];
  staggerTicks: number;
}

export type PlacementEntity = Pick<EntityState, 'kind' | 'dead' | 'removed' | 'position' | 'radius' | 'arenaSocketId'>;

export type PlacementResolution =
  | { valid: true; zoneId: string | null; groups: readonly PlannedSpawnGroup[] }
  | { valid: false; reason: 'outside-arena' | 'outside-territory' | 'formation-outside-territory' | 'spawn-blocked' };

export interface RuntimeSpawnResolution {
  position: WorldPoint;
  adjusted: boolean;
  reason: 'none' | 'inherited-bridge-corridor' | 'nearest-river-bank';
}

export function resolveRuntimeSpawnPosition(options: {
  position: WorldPoint;
  radius: number;
  movement: Movement;
  owner: PlayerId;
  arena: ArenaGameplayDefinition;
  inheritedRoute?: GroundRouteState;
}): RuntimeSpawnResolution {
  const { position, radius, movement, owner, arena, inheritedRoute } = options;
  if (movement !== 'ground' || !footprintIntersectsRiver(arena, position, radius)) {
    return { position: { ...position }, adjusted: false, reason: 'none' };
  }
  const bridge = inheritedRoute?.bridgeId
    ? arena.bridges.find((candidate) => candidate.id === inheritedRoute.bridgeId)
    : undefined;
  if (bridge) {
    const corridor = bridgeCorridor(bridge, radius);
    const x = Math.max(corridor.minimumX, Math.min(corridor.maximumX, position.x));
    return {
      position: { x, y: position.y },
      adjusted: x !== position.x,
      reason: x === position.x ? 'none' : 'inherited-bridge-corridor',
    };
  }

  const topY = arena.riverTop - radius;
  const bottomY = arena.riverBottom + radius;
  const topDistance = Math.abs(position.y - topY);
  const bottomDistance = Math.abs(position.y - bottomY);
  const y = topDistance === bottomDistance
    ? owner === 'player' ? bottomY : topY
    : topDistance < bottomDistance ? topY : bottomY;
  return { position: { x: position.x, y }, adjusted: true, reason: 'nearest-river-bank' };
}

const isPointInsideRect = (point: WorldPoint, bounds: Rect, inset = 0): boolean => (
  point.x >= bounds.x + inset
  && point.x <= bounds.x + bounds.width - inset
  && point.y >= bounds.y + inset
  && point.y <= bounds.y + bounds.height - inset
);

export const deploymentZoneForPoint = (
  zones: readonly DeploymentZoneDefinition[],
  point: WorldPoint,
): DeploymentZoneDefinition | undefined => zones.find((zone) => isPointInsideRect(point, zone.bounds));

const shiftIntoZone = (positions: readonly WorldPoint[], radius: number, zone: DeploymentZoneDefinition): WorldPoint[] => {
  if (!positions.length) return [];
  const minimumX = Math.min(...positions.map((point) => point.x));
  const maximumX = Math.max(...positions.map((point) => point.x));
  const minimumY = Math.min(...positions.map((point) => point.y));
  const maximumY = Math.max(...positions.map((point) => point.y));
  const left = zone.bounds.x + radius;
  const right = zone.bounds.x + zone.bounds.width - radius;
  const top = zone.bounds.y + radius;
  const bottom = zone.bounds.y + zone.bounds.height - radius;
  if (maximumX - minimumX > right - left || maximumY - minimumY > bottom - top) return positions.map((point) => ({ ...point }));
  const shiftX = minimumX < left ? left - minimumX : maximumX > right ? right - maximumX : 0;
  const shiftY = minimumY < top ? top - minimumY : maximumY > bottom ? bottom - maximumY : 0;
  return positions.map((point) => ({ x: point.x + shiftX, y: point.y + shiftY }));
};

export function activeDeploymentZones(
  arena: ArenaGameplayDefinition,
  playerId: PlayerId,
  entities: readonly Pick<EntityState, 'dead' | 'arenaSocketId'>[],
): readonly DeploymentZoneDefinition[] {
  const destroyedSockets = new Set<TowerSocketId>(entities.flatMap((entity) => entity.dead && entity.arenaSocketId ? [entity.arenaSocketId] : []));
  return arena.deploymentZones.filter((zone) => zone.owner === playerId && (!zone.unlockTowerSocketId || destroyedSockets.has(zone.unlockTowerSocketId)));
}

export function planSpawnGroups(
  _playerId: PlayerId,
  card: CardDefinition,
  position: WorldPoint,
  content: ContentSnapshot,
  arena: ArenaGameplayDefinition,
  zone?: DeploymentZoneDefinition,
): PlannedSpawnGroup[] {
  return card.mechanics.flatMap((mechanic) => {
    if (mechanic.type !== 'spawn-units') return [];
    return planSpawnDefinitions(mechanic.groups, position, content, arena, zone, card.id);
  });
}

export function planSpawnDefinitions(
  groups: readonly SpawnDefinition[],
  position: WorldPoint,
  content: ContentSnapshot,
  arena: ArenaGameplayDefinition,
  zone?: DeploymentZoneDefinition,
  sourceId = 'runtime-spawn',
): PlannedSpawnGroup[] {
  const bounds = arena.worldBounds;
  return groups.map((spawn) => {
      const definition = content.units.get(spawn.unitId);
      if (!definition) throw new Error(`Unknown unit ${spawn.unitId} for ${sourceId}`);
      const requestedPositions = Array.from({ length: spawn.count }, (_, index) => {
        const angle = spawn.count === 1 ? 0 : (Math.PI * 2 * index) / spawn.count - Math.PI / 2;
        return {
          x: clampToBounds(position.x + Math.cos(angle) * spawn.formationRadius, bounds.x, bounds.width, ARENA_EDGE_INSET),
          y: clampToBounds(position.y + Math.sin(angle) * spawn.formationRadius, bounds.y, bounds.height, ARENA_EDGE_INSET),
        };
      });
      const positions = zone ? shiftIntoZone(requestedPositions, definition.radius, zone) : requestedPositions.map((point) => ({ ...point }));
      return { definition, requestedPositions, positions, staggerTicks: spawn.staggerTicks ?? 0 };
  });
}

export function isSpawnAreaClear(
  groups: readonly PlannedSpawnGroup[],
  bounds: ArenaGameplayDefinition['worldBounds'],
  entities: readonly Pick<EntityState, 'kind' | 'dead' | 'removed' | 'position' | 'radius'>[],
): boolean {
  const obstacles = entities.filter((entity) => !entity.dead && !entity.removed && (entity.kind === 'tower' || entity.kind === 'building'));
  for (const group of groups) {
    for (const spawnPosition of group.positions) {
      if (!isPointInsideBounds(spawnPosition, bounds, Math.max(ARENA_EDGE_INSET, group.definition.radius))) return false;
      if (obstacles.some((obstacle) => Math.hypot(spawnPosition.x - obstacle.position.x, spawnPosition.y - obstacle.position.y) < (group.definition.radius + obstacle.radius) * 0.75)) return false;
    }
  }
  return true;
}

export function resolveCardPlacement(options: {
  playerId: PlayerId;
  card: CardDefinition;
  position: WorldPoint;
  content: ContentSnapshot;
  arena: ArenaGameplayDefinition;
  entities: readonly PlacementEntity[];
  activeZones?: readonly DeploymentZoneDefinition[];
}): PlacementResolution {
  const { playerId, card, position, content, arena, entities } = options;
  const bounds = arena.worldBounds;
  if (!isPointInsideBounds(position, bounds, ARENA_EDGE_INSET)) return { valid: false, reason: 'outside-arena' };

  if (card.placement === 'anywhere') {
    const groups = planSpawnGroups(playerId, card, position, content, arena);
    return isSpawnAreaClear(groups, bounds, entities)
      ? { valid: true, zoneId: null, groups }
      : { valid: false, reason: 'spawn-blocked' };
  }

  const zones = options.activeZones ?? activeDeploymentZones(arena, playerId, entities);
  const candidates = zones.filter((zone) => isPointInsideRect(position, zone.bounds));
  if (!candidates.length) return { valid: false, reason: 'outside-territory' };
  for (const zone of candidates) {
    const groups = planSpawnGroups(playerId, card, position, content, arena, zone);
    const formationInside = groups.every((group) => group.positions.every((point) => isPointInsideRect(point, zone.bounds, group.definition.radius)));
    if (!formationInside) continue;
    if (isSpawnAreaClear(groups, bounds, entities)) return { valid: true, zoneId: zone.id, groups };
  }
  return { valid: false, reason: candidates.some((zone) => {
    const groups = planSpawnGroups(playerId, card, position, content, arena, zone);
    return groups.every((group) => group.positions.every((point) => isPointInsideRect(point, zone.bounds, group.definition.radius)));
  }) ? 'spawn-blocked' : 'formation-outside-territory' };
}

export function isCardPlacementValid(options: Parameters<typeof resolveCardPlacement>[0]): boolean {
  return resolveCardPlacement(options).valid;
}
